import type {
  BriqpayCaptureRequest,
  BriqpayCreateSessionRequest,
  BriqpayDecisionRequest,
  BriqpayErrorResponse,
  BriqpayOrder,
  BriqpayRefundRequest,
  BriqpaySession,
  BriqpayUpdateMetadataRequest,
  BriqpayUpdateSessionRequest
} from 'ecosystem'
import { createLogger } from 'shared-utils'
import { apiCall, type ApiCallResponse } from '../../fetching'
import { BRIQPAY_API_URL, BRIQPAY_AUTH_HEADERS, BRIQPAY_MID } from '../constants'

const SESSION_URL = `${BRIQPAY_API_URL}/v3/session`

const logger = createLogger('payment', 'briqpay')

/**
 * Briqpay returns errors as `{ error: { code, message } }`. Several codes are worth
 * recognising on sight because they mean the *merchant portal* is misconfigured rather
 * than the payload being wrong:
 *
 * - `INVALID_CONFIG` — no payment method is active for this country/customerType pair.
 *   Connecting a PSP is not enough; it must be toggled Active under Payment Methods →
 *   Display, separately for Business and Consumer.
 * - `INVALID_DATA: Country not allowed` — the country is outside the merchant account's
 *   allowlist, which is not readable over the API.
 */
const briqpayApiCall = async <Data>(
  url: string,
  props: RequestInit
): Promise<ApiCallResponse<Data>> => {
  if (url.includes('undefined') || !BRIQPAY_MID) {
    throw new Error('Briqpay environment variables missing')
  }

  const body = (props.body as string) ?? ''

  const { data, error, response } = await apiCall<Data>(url, {
    ...props,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...BRIQPAY_AUTH_HEADERS,
      ...props.headers
    }
  })

  logger.log('[REQ]', props.method, url, body ? JSON.parse(body) : '')

  if (response && !response.ok) {
    let errorDetail = `Briqpay API error (${response.status})`
    const errorBody = data as BriqpayErrorResponse | null
    if (errorBody?.error?.code || errorBody?.error?.message) {
      errorDetail = `[${errorBody.error.code}] ${errorBody.error.message}`
    }
    logger.log(`[RES] ${response.status} error with detail`, props.method, url, errorDetail)

    return { data: null, error: errorDetail }
  }

  if (error) {
    logger.log(`[RES] ${response?.status} error`, props.method, url, error)
    return { data: null, error }
  }

  logger.log(`[RES] ${response?.status} success`, props.method, url, data)

  // 204 responses carry no body — `apiCall` leaves `data` null, which is success here.
  return { data, error: null }
}

/**
 * Create a payment session. The response carries the `htmlSnippet` to inject and the
 * `sessionId` that must be persisted as the SF order's `paymentId`.
 * @see https://developer.briqpay.com/docs/3.0.0/api/create-session/
 */
export const createSession = (payload: BriqpayCreateSessionRequest) =>
  briqpayApiCall<BriqpaySession>(SESSION_URL, {
    method: 'POST',
    body: JSON.stringify(payload)
  })

/**
 * Read a session. `fields` narrows the response, e.g. `'sessionId,htmlSnippet'`.
 * @see https://developer.briqpay.com/docs/3.0.0/api/read-session/
 */
export const readSession = (sessionId: string, fields?: string) =>
  briqpayApiCall<BriqpaySession>(
    `${SESSION_URL}/${sessionId}${fields ? `?fields=${encodeURIComponent(fields)}` : ''}`,
    { method: 'GET' }
  )

/**
 * Update the cart or the party data on a live session.
 *
 * Party data is module-owned: `billing`, `shipping` and `company` are rejected while the
 * matching module is loaded in the client, and the rejection fails the *whole* payload.
 * Keep cart syncs and party-data syncs in separate calls.
 *
 * @see https://developer.briqpay.com/docs/3.0.0/api/update-session/
 */
export const updateSession = (sessionId: string, payload: BriqpayUpdateSessionRequest) =>
  briqpayApiCall<BriqpaySession>(`${SESSION_URL}/${sessionId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload)
  })

/** Change `urls` or `references` mid-session without touching the cart. */
export const updateMetadata = (sessionId: string, payload: BriqpayUpdateMetadataRequest) =>
  briqpayApiCall<BriqpaySession>(`${SESSION_URL}/${sessionId}/metadata`, {
    method: 'PATCH',
    body: JSON.stringify(payload)
  })

/**
 * Answer a Decisions validation round-trip. The client must then call
 * `_briqpay.v3.resumeDecision()`.
 * @see https://developer.briqpay.com/docs/3.0.0/api/make-decision/
 */
export const makeDecision = (sessionId: string, payload: BriqpayDecisionRequest) =>
  briqpayApiCall<null>(`${SESSION_URL}/${sessionId}/decision`, {
    method: 'POST',
    body: JSON.stringify(payload)
  })

/** Cancel an order that has not been captured. */
export const cancelOrder = (sessionId: string) =>
  briqpayApiCall<{ status: 'cancel_pending' } | null>(`${SESSION_URL}/${sessionId}/order/cancel`, {
    method: 'POST'
  })

/**
 * Capture an authorised order. Briqpay takes a cart body rather than a bare amount, so a
 * partial capture is expressed as a reduced cart.
 *
 * Order-management surface — not wired into the storefront; this is here so OM has one
 * place to call into.
 */
export const captureOrder = (sessionId: string, payload: BriqpayCaptureRequest) =>
  briqpayApiCall<{ captureId?: string }>(`${SESSION_URL}/${sessionId}/order/capture`, {
    method: 'POST',
    body: JSON.stringify(payload)
  })

/** Refund a capture. Order-management surface — see {@link captureOrder}. */
export const refundCapture = (sessionId: string, payload: BriqpayRefundRequest) =>
  briqpayApiCall<{ refundId?: string }>(`${SESSION_URL}/${sessionId}/order/refund`, {
    method: 'POST',
    body: JSON.stringify(payload)
  })

/** Release the uncaptured remainder of a partially captured order. */
export const releaseRemaining = (sessionId: string) =>
  briqpayApiCall<null>(`${SESSION_URL}/${sessionId}/order/release-remaining`, {
    method: 'POST'
  })

/** Amend the cart of an already-completed order. Order-management surface. */
export const updateOrderCart = (sessionId: string, payload: { data: { order: BriqpayOrder } }) =>
  briqpayApiCall<null>(`${SESSION_URL}/${sessionId}/order/update/cart`, {
    method: 'PATCH',
    body: JSON.stringify(payload)
  })

/** Amend the references of an already-completed order. Order-management surface. */
export const updateOrderReferences = (
  sessionId: string,
  payload: { references: Record<string, string> }
) =>
  briqpayApiCall<null>(`${SESSION_URL}/${sessionId}/order/update/references`, {
    method: 'PATCH',
    body: JSON.stringify(payload)
  })
